import React, { FC, ReactNode } from 'react'
import Box from '@mui/system/Box'
import { CircularProgress, Grid, Link, Typography } from '@mui/material'
import format from 'date-fns/format'
import { SensorUpload } from 'src/api'
import { InnerLink } from 'src/components/ui'
import { getSensorUploadFileName, SensorUploadStatusChip } from 'src/components/columns/sensorUpload'
import { getSensorUploadStatusMeta } from 'src/components/columns/sensorUploadHelpers'

interface SensorUploadInfoProps {
  upload: SensorUpload
  onDownloadSourceFile: () => void
  isDownloading?: boolean
}

interface InfoItem {
  name: string
  value: ReactNode
}

export const SensorUploadInfo: FC<SensorUploadInfoProps> = ({ upload, onDownloadSourceFile, isDownloading = false }) => {
  const { status, errorMessage, createdCount, skippedCount, createdAt, author, file } = upload
  const { description } = getSensorUploadStatusMeta(status)

  const items: InfoItem[] = [
    {
      name: 'File name',
      value: file ? (
        <>
          <Link component="button" type="button" variant="body1" disabled={isDownloading} onClick={onDownloadSourceFile} sx={{ verticalAlign: 'baseline' }}>
            {getSensorUploadFileName(upload)}
          </Link>
          {isDownloading && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </>
      ) : (
        '-'
      )
    },
    { name: 'Author', value: author?.id ? <InnerLink to={`/users/${author.id}`}>{author.name}</InnerLink> : author?.name || '-' },
    {
      name: 'Status',
      value: (
        <>
          <SensorUploadStatusChip status={status} errorMessage={errorMessage} />
          {(description || errorMessage) && (
            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
              {[description, errorMessage].filter(Boolean).join('. ')}
            </Typography>
          )}
        </>
      )
    },
    { name: 'Sensors created', value: createdCount ?? '-' },
    { name: 'Sensors skipped', value: skippedCount ?? '-' },
    { name: 'Imported at', value: createdAt ? format(new Date(createdAt), 'PPpp') : '-' }
  ]

  return (
    <Box width="100%" pt={2}>
      <Grid container spacing={0}>
        {items.map((item, index) => (
          <Grid key={index} item xs={12} sx={{ py: 0.5 }}>
            <Typography variant="body1" component="div">
              <Typography variant="body1" fontWeight={500} display="inline">
                {item.name}:
              </Typography>{' '}
              {item.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}
